import React, { useState, useEffect, useContext } from "react";
import { Table, Spinner, Button, Modal, Form, Alert, Badge } from "react-bootstrap";
import { FiEye } from "react-icons/fi";
import { FaWallet, FaFileInvoice, FaMoneyBillWave, FaCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { TranslatorContext } from "../../../../context/Translator";

const AppointmentsTab = ({ appointments, loading, customerId }) => {
    const navigate = useNavigate();
    const { t } = useContext(TranslatorContext);

    // Customer balance and pending appointments states
    const [customerBalance, setCustomerBalance] = useState(null);
    const [showPendingAppointments, setShowPendingAppointments] = useState(false);
    const [pendingAppointments, setPendingAppointments] = useState([]);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [showCustomerPaymentModal, setShowCustomerPaymentModal] = useState(false);
    const [selectedAppointmentForPayment, setSelectedAppointmentForPayment] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [customerPaymentForm, setCustomerPaymentForm] = useState({
        payment_method_id: '',
        amount: 0,
        payment_date: getTodayDate()
    });

    const role = localStorage.getItem("role");
    const isAdmin = role === 'admin' || role === 'Admin';

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    const canManagePayments = () => {
        return hasPermission("Appointment Payments Manage");
    };

    // Get today's date in YYYY-MM-DD format
    function getTodayDate() {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";

        const date = new Date(dateString);

        return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }).format(date);
    };

    const handleViewAppointment = (appointmentId) => {
        navigate(`/appointments/${appointmentId}`);
    };

    // Fetch customer balance when customerId is provided
    useEffect(() => {
        if (customerId) {
            fetchCustomerBalance(customerId);
            fetchPaymentMethods();
        } else {
            setCustomerBalance(null);
            setPendingAppointments([]);
            setShowPendingAppointments(false);
        }
    }, [customerId]);

    const fetchCustomerBalance = async (custId) => {
        setLoadingBalance(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/appointments/ledger/${custId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            setCustomerBalance(response.data);
        } catch (error) {
            console.error("Error fetching customer balance:", error);
            setCustomerBalance(null);
        } finally {
            setLoadingBalance(false);
        }
    };

    const fetchPendingAppointments = async (custId) => {
        setLoadingBalance(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/appointments/pending/${custId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            if (response.data.status) {
                setPendingAppointments(response.data.appointments || []);
            }
        } catch (error) {
            console.error("Error fetching pending appointments:", error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t('Failed to fetch pending appointments'),
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoadingBalance(false);
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/payment-methods`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                if (Array.isArray(response.data)) {
                    setPaymentMethods(response.data);
                } else if (response.data.paymentMethods && Array.isArray(response.data.paymentMethods)) {
                    setPaymentMethods(response.data.paymentMethods);
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    setPaymentMethods(response.data.data);
                } else {
                    setPaymentMethods([]);
                }
            }
        } catch (error) {
            console.error("Error fetching payment methods:", error);
            setPaymentMethods([]);
        }
    };

    const handleShowPendingAppointments = async () => {
        if (!customerId) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('No customer selected'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (!showPendingAppointments) {
            await fetchPendingAppointments(customerId);
            setShowPendingAppointments(true);
        } else {
            setShowPendingAppointments(false);
        }
    };

    const handleMakeCustomerPayment = (appointment) => {
        setSelectedAppointmentForPayment(appointment);
        setCustomerPaymentForm({
            payment_method_id: '',
            amount: appointment.due,
            payment_date: getTodayDate()
        });
        setShowCustomerPaymentModal(true);
    };

    const handleCustomerPaymentSubmit = async () => {
        if (!customerPaymentForm.payment_method_id) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Please select payment method'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (customerPaymentForm.amount <= 0) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Amount must be greater than 0'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        setSubmitting(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/appointments/allocate-payment`,
                {
                    customer_id: customerId,
                    amount: customerPaymentForm.amount,
                    payment_method_id: customerPaymentForm.payment_method_id,
                    payment_date: customerPaymentForm.payment_date
                },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Payment allocated successfully'),
                    showConfirmButton: false,
                    timer: 2000
                });

                setShowCustomerPaymentModal(false);
                // Refresh balance and pending appointments
                await fetchCustomerBalance(customerId);
                if (showPendingAppointments) {
                    await fetchPendingAppointments(customerId);
                }

                if (response.data.remaining_unallocated > 0) {
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'info',
                        title: `${t('Remaining amount')}: ${response.data.remaining_unallocated}`,
                        showConfirmButton: false,
                        timer: 3000
                    });
                }
            }
        } catch (error) {
            console.error("Error making payment:", error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: error.response?.data?.message || t('Payment failed'),
                showConfirmButton: false,
                timer: 3000
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading appointments...</p>
            </div>
        );
    }

    return (
        <>
            {/* Customer Balance Display - Professional Card */}
            {customerBalance && customerBalance.total_balance > 0 && (
                <div className="mb-4" style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    boxShadow: '0 8px 32px rgba(15, 52, 96, 0.35)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background decorative circles */}
                    <div style={{
                        position: 'absolute', top: '-40px', right: '-40px',
                        width: '150px', height: '150px', borderRadius: '50%',
                        background: 'rgba(255,255,255,0.04)', pointerEvents: 'none'
                    }} />
                    <div style={{
                        position: 'absolute', bottom: '-20px', left: '30%',
                        width: '100px', height: '100px', borderRadius: '50%',
                        background: 'rgba(255,255,255,0.03)', pointerEvents: 'none'
                    }} />

                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        {/* Left: Balance info */}
                        <div className="d-flex align-items-center gap-3">
                            <div style={{
                                background: 'rgba(255,193,7,0.15)',
                                border: '1px solid rgba(255,193,7,0.3)',
                                borderRadius: '12px',
                                width: '52px', height: '52px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <FaWallet size={22} color="#ffc107" />
                            </div>
                            <div>
                                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 500, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '2px' }}>
                                    {t('Customer Balance')}
                                </div>
                                <div style={{ color: '#ffffff', fontSize: '26px', fontWeight: 700, lineHeight: 1.1 }}>
                                    {parseFloat(customerBalance.total_balance).toLocaleString()}
                                </div>
                            </div>
                        </div>

                        {/* Middle: Stats chips */}
                        <div className="d-flex gap-2 flex-wrap">
                            {customerBalance.total_due != null && (
                                <div style={{
                                    background: 'rgba(255,255,255,0.08)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    borderRadius: '10px', padding: '8px 16px', textAlign: 'center'
                                }}>
                                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginBottom: '2px' }}>{t('Total Due')}</div>
                                    <div style={{ color: '#ffc107', fontWeight: 700, fontSize: '18px' }}>
                                        {parseFloat(customerBalance.total_due).toLocaleString()}
                                    </div>
                                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>{t('outstanding')}</div>
                                </div>
                            )}
                        </div>

                        {/* Right: Action button */}
                        {canManagePayments() && (
                            <Button
                                onClick={handleShowPendingAppointments}
                                style={{
                                    background: 'linear-gradient(135deg, #ffc107, #ff9800)',
                                    border: 'none',
                                    borderRadius: '10px',
                                    padding: '10px 20px',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    color: '#1a1a2e',
                                    boxShadow: '0 4px 15px rgba(255,193,7,0.3)',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <FaFileInvoice className="me-2" size={14} />
                                {t('View Pending Appointments')}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Appointments Table */}
            {appointments.length === 0 ? (
                <div className="text-center py-5">
                    <p className="text-muted">No appointments found for this customer.</p>
                </div>
            ) : (
                <div className="table-responsive">
                    <Table
                        responsive
                        style={{
                            width: "100%",
                            minWidth: "1100px",
                            tableLayout: "fixed",
                        }}
                    >
                        <colgroup>
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "14%" }} />

                            {/* Services column */}
                            <col style={{ width: isAdmin ? "26%" : "36%" }} />

                            {/* Amount column */}
                            {isAdmin && <col style={{ width: "10%" }} />}

                            {/* Actions column */}
                            <col style={{ width: "8%" }} />
                        </colgroup>

                        <thead style={{ backgroundColor: "#347fc1", color: "#fff" }}>
                            <tr>
                                <th
                                    style={{
                                        border: "none",
                                        padding: "12px 16px",
                                        textAlign: "left",
                                    }}
                                >
                                    Appointment #
                                </th>

                                <th
                                    style={{
                                        border: "none",
                                        padding: "12px 16px",
                                        textAlign: "left",
                                    }}
                                >
                                    Pet Name
                                </th>

                                <th
                                    style={{
                                        border: "none",
                                        padding: "12px 16px",
                                        textAlign: "left",
                                    }}
                                >
                                    Date
                                </th>

                                <th
                                    style={{
                                        border: "none",
                                        padding: "12px 16px",
                                        textAlign: "left",
                                    }}
                                >
                                    Status
                                </th>

                                <th
                                    style={{
                                        border: "none",
                                        padding: "12px 16px",
                                        textAlign: "left",
                                    }}
                                >
                                    Services
                                </th>

                                {isAdmin && (
                                    <th
                                        style={{
                                            border: "none",
                                            padding: "12px 16px",
                                            textAlign: "right",
                                        }}
                                    >
                                        Amount
                                    </th>
                                )}

                                <th
                                    style={{
                                        border: "none",
                                        padding: "12px 16px",
                                        textAlign: "center",
                                    }}
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {appointments.map((appointment) => {
                                const totalAmount = (appointment.detail ?? []).reduce(
                                    (total, item) =>
                                        total +
                                        Number(item?.formatted_detail?.price || 0),
                                    0
                                );

                                return (
                                    <tr
                                        key={appointment.id}
                                        style={{
                                            borderBottom: "1px solid #ececec",
                                        }}
                                    >
                                        {/* Appointment */}
                                        <td
                                            style={{
                                                border: "none",
                                                padding: "16px",
                                                verticalAlign: "middle",
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            <strong>
                                                {appointment.appointment_number ||
                                                    `APT-${appointment.id}`}
                                            </strong>

                                            <br />

                                            <span
                                                className="text-muted"
                                                style={{ fontSize: "12px" }}
                                            >
                                                Dr. {appointment.doctor?.name || "N/A"}
                                            </span>
                                        </td>

                                        {/* Pet */}
                                        <td
                                            style={{
                                                border: "none",
                                                padding: "16px",
                                                verticalAlign: "middle",
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            {appointment.pet?.name || "N/A"}
                                        </td>

                                        {/* Date */}
                                        <td
                                            style={{
                                                border: "none",
                                                padding: "16px",
                                                verticalAlign: "middle",
                                            }}
                                        >
                                            {formatDate(appointment.appointment_date)}

                                            <br />

                                            {appointment.appointment_time_slot || "N/A"}
                                        </td>

                                        {/* Status */}
                                        <td
                                            style={{
                                                border: "none",
                                                padding: "16px",
                                                verticalAlign: "middle",
                                            }}
                                        >
                                            <span
                                                className={`badge bg-${appointment.status === "scheduled"
                                                        ? "primary"
                                                        : appointment.status === "completed"
                                                            ? "success"
                                                            : appointment.status === "cancelled"
                                                                ? "danger"
                                                                : "secondary"
                                                    }`}
                                                style={{
                                                    padding: "6px 12px",
                                                    fontSize: "14px",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {appointment.status || "N/A"}
                                            </span>
                                        </td>

                                        {/* Services */}
                                        <td
                                            style={{
                                                border: "none",
                                                padding: "16px",
                                                verticalAlign: "middle",
                                            }}
                                        >
                                            {appointment.detail?.length ? (
                                                appointment.detail.map((item, index) => {
                                                    const detail =
                                                        item?.formatted_detail;

                                                    if (!detail) return null;

                                                    return (
                                                        <div
                                                            key={item.id || index}
                                                            className="mb-2 px-2 py-1 rounded"
                                                            style={{
                                                                display: "grid",
                                                                gridTemplateColumns:
                                                                    "minmax(0, 1fr) auto",
                                                                alignItems: "start",
                                                                columnGap: "12px",
                                                                width: "100%",
                                                                fontSize: "12px",
                                                            }}
                                                        >
                                                            {/* Service name */}
                                                            <span
                                                                style={{
                                                                    minWidth: 0,
                                                                    fontWeight: 500,
                                                                    color: "#333",
                                                                    lineHeight: "18px",
                                                                    whiteSpace: "normal",
                                                                    overflowWrap: "anywhere",
                                                                }}
                                                            >
                                                                {detail.name || "N/A"}
                                                            </span>

                                                            {/* Service price */}
                                                            <span
                                                                style={{
                                                                    fontWeight: 600,
                                                                    color: "#555",
                                                                    textAlign: "right",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {Number(
                                                                    detail.price || 0
                                                                ).toFixed(2)}
                                                            </span>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <span className="text-muted">
                                                    No Services
                                                </span>
                                            )}
                                        </td>

                                        {/* Total Amount */}
                                        {isAdmin && (
                                            <td
                                                style={{
                                                    border: "none",
                                                    padding: "16px",
                                                    textAlign: "right",
                                                    verticalAlign: "middle",
                                                    fontWeight: 700,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {totalAmount.toFixed(2)}
                                            </td>
                                        )}

                                        {/* Actions */}
                                        <td
                                            style={{
                                                border: "none",
                                                padding: "16px",
                                                textAlign: "center",
                                                verticalAlign: "middle",
                                            }}
                                        >
                                            <Button
                                                variant="link"
                                                className="p-0 text-primary"
                                                onClick={() =>
                                                    handleViewAppointment(
                                                        appointment.id
                                                    )
                                                }
                                                style={{
                                                    textDecoration: "none",
                                                }}
                                            >
                                                <FiEye size={20} />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Customer Payment Modal for Pending Appointments */}
            <Modal show={showCustomerPaymentModal} onHide={() => setShowCustomerPaymentModal(false)} size="md">
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>{t('Make Payment')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAppointmentForPayment && (
                        <>
                            <div className="mb-3">
                                <strong>{t('Appointment #')}:</strong> {selectedAppointmentForPayment.appointment_number}<br />
                                <strong>{t('Due Amount')}:</strong> <span className="text-danger fw-bold">{parseFloat(selectedAppointmentForPayment.due).toLocaleString()}</span>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Payment Method')} <span className="text-danger">*</span></Form.Label>
                                <select
                                    className="form-control"
                                    value={customerPaymentForm.payment_method_id}
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, payment_method_id: e.target.value })}
                                >
                                    <option value="">{t('Select Payment Method')}</option>
                                    {paymentMethods.map(pm => (
                                        <option key={pm.id} value={pm.id}>{pm.name}</option>
                                    ))}
                                </select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Amount')} <span className="text-danger">*</span></Form.Label>
                                <input
                                    type="number"
                                    className="form-control"
                                    step="0.01"
                                    min="0.01"
                                    max={selectedAppointmentForPayment.due}
                                    value={customerPaymentForm.amount}
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, amount: parseFloat(e.target.value) || 0 })}
                                />
                                <small className="text-muted">{t('Max amount')}: {parseFloat(selectedAppointmentForPayment.due).toLocaleString()}</small>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Payment Date')} <span className="text-danger">*</span></Form.Label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={customerPaymentForm.payment_date}
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, payment_date: e.target.value })}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="p-3">
                    <Button variant="secondary" onClick={() => setShowCustomerPaymentModal(false)}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleCustomerPaymentSubmit} disabled={submitting}>
                        {submitting ? <Spinner animation="border" size="sm" /> : t('Pay Now')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Pending Appointments Modal */}
            <Modal
                show={showPendingAppointments}
                onHide={() => setShowPendingAppointments(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton style={{
                    background: 'linear-gradient(135deg, #1a1a2e, #0f3460)',
                    borderBottom: 'none',
                    padding: '20px 24px'
                }}>
                    <Modal.Title style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>
                        <FaFileInvoice className="me-2" style={{ color: '#ffc107' }} />
                        {t('Pending Appointments')}
                        {pendingAppointments.length > 0 && (
                            <Badge style={{
                                background: 'rgba(255,193,7,0.2)',
                                color: '#ffc107',
                                border: '1px solid rgba(255,193,7,0.3)',
                                marginLeft: '10px',
                                fontWeight: 600,
                                fontSize: '12px',
                                padding: '4px 10px',
                                borderRadius: '20px'
                            }}>
                                {pendingAppointments.length} {t('pending')}
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: '0', background: '#f8f9fa' }}>
                    {loadingBalance ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">{t('Loading...')}</p>
                        </div>
                    ) : pendingAppointments.length === 0 ? (
                        <div className="text-center py-5">
                            <FaFileInvoice size={40} className="text-muted mb-3" />
                            <p className="text-muted">{t('No pending appointments found')}</p>
                        </div>
                    ) : (
                        <div style={{ padding: '20px' }}>
                            {/* Summary bar */}
                            <div style={{
                                borderRadius: '12px',
                                padding: '16px 20px',
                                marginBottom: '20px',
                                display: 'flex',
                                gap: '30px',
                                flexWrap: 'wrap'
                            }}>
                                <div>
                                    <div style={{ color: 'black', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{t('Total Due')}</div>
                                    <div style={{ color: '#ff6b6b', fontWeight: 700, fontSize: '20px' }}>
                                        {pendingAppointments.reduce((s, a) => s + parseFloat(a.due || 0), 0).toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: 'black', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{t('Total Paid')}</div>
                                    <div style={{ color: '#4ade80', fontWeight: 700, fontSize: '20px' }}>
                                        {pendingAppointments.reduce((s, a) => s + parseFloat(a.paid || 0), 0).toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: 'black', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{t('Appointments')}</div>
                                    <div style={{ color: '#ffc107', fontWeight: 700, fontSize: '20px' }}>{pendingAppointments.length}</div>
                                </div>
                            </div>

                            {/* Table */}
                            <div>
                                <table className="table mb-0">
                                    <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                            <th>{t('Appointment #')}</th>
                                            <th>{t('Date')}</th>
                                            <th>{t('Total')}</th>
                                            <th>{t('Paid')}</th>
                                            <th>{t('Due')}</th>
                                            <th>{t('Status')}</th>
                                            <th>{t('Action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingAppointments.map((appointment, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '14px 16px', fontWeight: 600, color: '#1e293b', border: 'none' }}>
                                                    <FaCalendarAlt className="me-2" size={12} style={{ color: '#94a3b8' }} />
                                                    {appointment.appointment_number}
                                                </td>
                                                <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '13px', border: 'none' }}>
                                                    {moment(appointment.appointment_date).format('DD MMM hh:mm A')}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 600, color: '#1e293b', border: 'none' }}>
                                                    {parseFloat(appointment.total).toLocaleString()}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 600, color: '#16a34a', border: 'none' }}>
                                                    {parseFloat(appointment.paid).toLocaleString()}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#dc2626', border: 'none' }}>
                                                    {parseFloat(appointment.due).toLocaleString()}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'center', border: 'none' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '4px 12px',
                                                        borderRadius: '20px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        ...(appointment.status === 'partial'
                                                            ? { background: '#fef3c7', color: '#d97706' }
                                                            : { background: '#fee2e2', color: '#dc2626' })
                                                    }}>
                                                        {appointment.status === 'partial' ? t('Partial') : t('Unpaid')}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'center', border: 'none' }}>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => {
                                                            setShowPendingAppointments(false);
                                                            handleMakeCustomerPayment(appointment);
                                                        }}
                                                        disabled={!canManagePayments()}
                                                        style={{
                                                            background: 'linear-gradient(135deg, #0f3460, #1a1a2e)',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            fontSize: '12px',
                                                            fontWeight: 600,
                                                            padding: '6px 14px'
                                                        }}
                                                    >
                                                        <FaMoneyBillWave className="me-1" size={11} />
                                                        {t('Pay Now')}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer style={{ background: '#f8f9fa', borderTop: '1px solid #e2e8f0', padding: '12px 20px' }}>
                    <Button variant="secondary" size="sm" onClick={() => setShowPendingAppointments(false)}>
                        {t('Close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AppointmentsTab;